import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import usePageMeta from '../hooks/usePageMeta';
import useFeatureFlag from '../hooks/useFeatureFlag';
import { trackEvent } from '../analytics';
import {
  PUBLIC_DEFAULT_STATE,
  DEFAULT_CALCULATOR_STATE,
  DRIVE_DEFAULTS,
  FPS_OPTIONS,
  SCENARIOS,
  SOLVE_FOR_OPTIONS,
  DURATION_UNITS,
  PACE_CEILING,
  modeToSolveFor,
  solveForToMode,
  scenarioSwitchPatch,
} from '../lib/timelapseCalc';
import { MOTIONPACE_MARKETING_ENABLED } from '../lib/motionPaceMarketing';
import TimelapseCalculator from './TimelapseCalculator';
import SaveCalculationDialog from './SaveCalculationDialog';
import './TimelapseCalculatorPage.css';

// ---------- Example chips ----------
// One short row per scenario, replacing v1's six-card preset grid: in 14 days
// of analytics not one visitor tapped a preset card, and the grid pushed the
// answer another screen down on a phone. Each entry is a partial input patch,
// merged over whatever the visitor already has, so switching examples never
// resets their frame rate or storage choice.
//
// The four long-term ids (construction, renovation, deck_build, garden) are the
// v1 ids and must not be renamed: they are the values already in the
// calculator_preset_selected event stream.
const EXAMPLES = {
  shoot: [
    { id: 'sunset', label: 'Sunset', sub: '2 hr, 5 s', inputs: { durationValue: 2, durationUnit: 'hours', intervalSeconds: 5, deadTimeFraction: 0.25 } },
    { id: 'clouds', label: 'Clouds', sub: '30 min, 1 s', inputs: { durationValue: 30, durationUnit: 'minutes', intervalSeconds: 1, deadTimeFraction: 0.2 } },
    { id: 'print_3d', label: '3D print', sub: '6 hr, 10 s', inputs: { durationValue: 6, durationUnit: 'hours', intervalSeconds: 10, deadTimeFraction: 0.1 } },
    { id: 'workshop_afternoon', label: 'Workshop afternoon', sub: '4 hr, 5 s', inputs: { durationValue: 4, durationUnit: 'hours', intervalSeconds: 5, deadTimeFraction: 0.3 } },
  ],
  project: [
    {
      id: 'construction',
      label: 'Construction',
      sub: '6 months, weekdays',
      inputs: {
        durationValue: 6,
        durationUnit: 'months',
        weekdayMask: [true, true, true, true, true, false, false],
        dailyStartMin: 7 * 60,
        dailyEndMin: 17 * 60,
        is24h: false,
        intervalSeconds: 10 * 60,
        deadTimeFraction: 0.4,
      },
    },
    {
      id: 'renovation',
      label: 'Renovation',
      sub: '6 weeks, 5 min',
      inputs: {
        durationValue: 6,
        durationUnit: 'weeks',
        weekdayMask: [true, true, true, true, true, true, true],
        dailyStartMin: 8 * 60,
        dailyEndMin: 18 * 60,
        is24h: false,
        intervalSeconds: 5 * 60,
        deadTimeFraction: 0.35,
      },
    },
    {
      id: 'deck_build',
      label: 'Deck build',
      sub: '2 weeks, 2 min',
      inputs: {
        durationValue: 2,
        durationUnit: 'weeks',
        weekdayMask: [true, true, true, true, true, true, true],
        dailyStartMin: 8 * 60,
        dailyEndMin: 18 * 60,
        is24h: false,
        intervalSeconds: 120,
        deadTimeFraction: 0.3,
      },
    },
    {
      id: 'garden',
      label: 'Garden',
      sub: '3 months, 30 min',
      inputs: {
        durationValue: 3,
        durationUnit: 'months',
        weekdayMask: [true, true, true, true, true, true, true],
        dailyStartMin: 6 * 60,
        dailyEndMin: 20 * 60,
        is24h: false,
        intervalSeconds: 30 * 60,
        deadTimeFraction: 0.2,
      },
    },
  ],
  drive: [
    { id: 'city_commute', label: 'City commute', sub: '12 mi at 25 mph, 1 s', inputs: { distanceValue: 12, distanceUnit: 'mi', speedValue: 25, intervalSeconds: 1, deadTimeFraction: 0.25 } },
    { id: 'road_trip', label: 'Road trip', sub: '300 mi at 65 mph, 2 s', inputs: { distanceValue: 300, distanceUnit: 'mi', speedValue: 65, intervalSeconds: 2, deadTimeFraction: 0.1 } },
    { id: 'scenic_drive', label: 'Scenic drive', sub: '60 mi at 40 mph, 1 s', inputs: { distanceValue: 60, distanceUnit: 'mi', speedValue: 40, intervalSeconds: 1, deadTimeFraction: 0.15 } },
  ],
};

// ---------- URL query <-> state ----------
// Every key a shared link can carry. Used both to read a link and to decide
// whether a query string is a shared calculation at all (see below).
const QUERY_KEYS = ['sc', 'sf', 'mode', 'dv', 'du', 'wm', 'ds', 'de', 'h24', 'dist', 'dunit', 'spd', 'int', 'tgt', 'fps', 'dead', 'mb'];

export function stateFromQuery(qp, fallback) {
  const s = { ...fallback };
  const num = (k, coerce = parseFloat) => {
    const raw = qp.get(k);
    if (raw == null) return undefined;
    const v = coerce(raw);
    return Number.isFinite(v) ? v : undefined;
  };
  const setIf = (k, v) => { if (v !== undefined) s[k] = v; };

  const sc = qp.get('sc');
  if (SCENARIOS.includes(sc)) {
    s.scenario = sc;
  } else if (QUERY_KEYS.some((k) => qp.get(k) != null)) {
    // A link written before scenarios existed. Every one of those describes a
    // long-term project, because that is all v1 could describe — so an old
    // link keeps computing what its sender saw. A bare landing (no query at
    // all) falls through to the page default instead.
    s.scenario = 'project';
  }

  setIf('durationValue', num('dv'));
  const du = qp.get('du');
  if (DURATION_UNITS.includes(du)) s.durationUnit = du;
  const wm = qp.get('wm');
  if (wm && /^[01]{7}$/.test(wm)) s.weekdayMask = wm.split('').map((c) => c === '1');
  setIf('dailyStartMin', num('ds', (x) => parseInt(x, 10)));
  setIf('dailyEndMin', num('de', (x) => parseInt(x, 10)));
  const d24 = qp.get('h24');
  if (d24 === '1') s.is24h = true;
  if (d24 === '0') s.is24h = false;
  setIf('distanceValue', num('dist'));
  const dunit = qp.get('dunit');
  if (dunit === 'mi' || dunit === 'km') s.distanceUnit = dunit;
  setIf('speedValue', num('spd'));
  setIf('intervalSeconds', num('int'));
  setIf('targetVideoSeconds', num('tgt'));
  const fps = num('fps', (x) => parseInt(x, 10));
  if (fps && FPS_OPTIONS.includes(fps)) s.fps = fps;
  setIf('deadTimeFraction', num('dead'));
  setIf('mbPerFrame', num('mb'));

  // A hand-written /tools/timelapse-calculator?sc=drive carries no driving
  // numbers, and the page defaults (a 5 s interval meant for a sunset) put a
  // frame every 100 m of road, which is the one setting the tool warns about.
  // So the driving defaults fill in for any drive key the link omits. A link
  // shared FROM this page always carries all of them, so it is never touched.
  if (s.scenario === 'drive') {
    const driveQueryKeys = {
      distanceValue: 'dist',
      distanceUnit: 'dunit',
      speedValue: 'spd',
      intervalSeconds: 'int',
      deadTimeFraction: 'dead',
      targetVideoSeconds: 'tgt',
    };
    Object.keys(driveQueryKeys).forEach((k) => {
      if (qp.get(driveQueryKeys[k]) == null) s[k] = DRIVE_DEFAULTS[k];
    });
  }

  // The same trap on a hand-written ?sc=project: the page defaults describe a
  // two-hour shoot, and "hours" is not a unit the long-term form can even show,
  // so that link opened on a duration the calculator had to reject. Any project
  // key the link omits falls back to the long-term defaults. Only an explicit
  // sc=project is touched, so a pre-scenario `mode=` link still computes
  // exactly what its sender saw.
  if (sc === 'project') {
    const projectQueryKeys = {
      durationValue: 'dv',
      durationUnit: 'du',
      weekdayMask: 'wm',
      dailyStartMin: 'ds',
      dailyEndMin: 'de',
      is24h: 'h24',
      intervalSeconds: 'int',
      deadTimeFraction: 'dead',
      targetVideoSeconds: 'tgt',
    };
    Object.keys(projectQueryKeys).forEach((k) => {
      if (qp.get(projectQueryKeys[k]) != null) return;
      const v = DEFAULT_CALCULATOR_STATE[k];
      s[k] = Array.isArray(v) ? [...v] : v;
    });
  }

  const sf = qp.get('sf');
  const mode = qp.get('mode');
  if (SOLVE_FOR_OPTIONS.includes(sf)) {
    s.solveFor = sf;
  } else if (mode === 'interval' || mode === 'target') {
    s.solveFor = modeToSolveFor(mode);
  }

  // Last: a hand-written link can name a solve direction or a duration unit the
  // chosen scenario has no control for — `?sc=drive&sf=duration` (driving has no
  // "recording time" question, the drive length IS the input) or
  // `?sc=shoot&du=months` (the shoot length switch only offers min/hr/days).
  // Both leave a segmented control with nothing selected, and the drive case is
  // worse: the answer silently replaces the drive with `frames x interval` and
  // reports a recording time the distance and speed on screen played no part in.
  // These are the same rewrites the scenario tabs apply when you switch by hand,
  // so reuse that one rule rather than keeping a second copy of it here.
  Object.assign(s, scenarioSwitchPatch(s, s.scenario));

  s.mode = solveForToMode(s.solveFor);
  return s;
}

export function stateToQuery(state) {
  return {
    sc: state.scenario,
    sf: state.solveFor,
    // The legacy key stays in the link so a URL shared from this page still
    // opens correctly in a cached v1 bundle.
    mode: solveForToMode(state.solveFor),
    dv: String(state.durationValue),
    du: state.durationUnit,
    wm: state.weekdayMask.map((b) => (b ? '1' : '0')).join(''),
    ds: String(state.dailyStartMin),
    de: String(state.dailyEndMin),
    h24: state.is24h ? '1' : '0',
    dist: String(state.distanceValue),
    dunit: state.distanceUnit,
    spd: String(state.speedValue),
    int: String(state.intervalSeconds),
    tgt: String(state.targetVideoSeconds),
    fps: String(state.fps),
    dead: String(state.deadTimeFraction),
    mb: String(state.mbPerFrame),
  };
}

// ---------- FAQ content (shared with jsonLd) ----------
// `a` is the plain-text answer and is what goes into the FAQPage JSON-LD, so it
// has to stand on its own. `link` renders after it on the page only — a crawler
// reading the structured data still gets a complete answer without it.
const FAQ = [
  {
    q: 'How do I calculate time lapse length?',
    a: 'Final video length = recording time ÷ interval ÷ frame rate. Record for 8 hours (28,800 seconds) at one photo every 5 seconds and you capture 5,760 frames; played at 30 fps that is 3 minutes 12 seconds. The calculator above runs the same math with your daily shooting window and downtime included.',
    link: {
      id: 'howlong',
      to: '/guides/how-long-is-a-time-lapse',
      label: 'See the full conversion charts →',
      navTarget: 'howlong_from_calculator_faq',
    },
  },
  {
    q: 'How long is a 30-second time lapse in real time?',
    a: 'Multiply the other way: recording time = video length × frame rate × interval. A 30-second clip at 30 fps needs 900 frames, so at one photo every 2 seconds you record for 1,800 seconds, which is 30 minutes. At one photo every 10 seconds the same clip needs 9,000 seconds of recording, which is 2 hours 30 minutes. Set the calculator to "Recording time" to solve it for your own interval.',
    link: {
      id: 'howlong30',
      to: '/guides/how-long-is-a-time-lapse',
      label: 'Conversion charts for other lengths →',
      navTarget: 'howlong30_from_calculator_faq',
    },
  },
  {
    q: 'How do I calculate a driving time lapse?',
    a: 'Start from the drive, not the clock. Distance ÷ average speed gives the driving time, and that drives the frame count the same way a shoot duration does: a 30-mile drive at 45 mph takes 2,400 seconds, so one frame a second is 2,400 frames, which plays for 80 seconds at 30 fps. Pick the Driving scenario above and enter distance and average speed instead of a duration.',
  },
  {
    q: 'What interval should I use for a driving time lapse?',
    a: 'Think in metres of road per frame rather than seconds. At 45 mph you cover about 20 metres every second, so a 1-second interval puts a frame every 20 metres and the road reads as a smooth pull. Doubling the interval doubles that gap and the clip starts to strobe. At motorway speeds try 0.5 seconds; on slow city streets 1 to 2 seconds is plenty. The calculator prints the metres per frame for your numbers.',
  },
  {
    q: 'What is the best interval for a construction timelapse?',
    a: 'For most multi-month construction builds, capturing one frame every 5–15 minutes hits the sweet spot: fast enough to show visible progress each day, slow enough to keep storage sane. Our Construction example uses 10 minutes as a starting point — dial the interval until the final video length matches your goal.',
  },
  {
    q: 'How much storage do I need for a 6-month timelapse?',
    a: 'It depends on interval and image size. Enter your duration, window, and MB-per-frame above and the calculator gives an exact number. As a rough guide: a 6-month build shot 10 hours a day at a 10-minute interval with 5 MB JPEGs uses about 30–40 GB of source footage.',
  },
  {
    q: 'What FPS should I use for a timelapse?',
    a: '24 FPS gives a cinematic feel, 30 FPS is the safe web default, and 60 FPS is worth it only when there is genuinely fast on-screen motion. For most long-term timelapses, 30 FPS is the right answer.',
  },
  {
    q: 'Does trimming the stillness really matter?',
    a: 'Yes — it is often the difference between a 40-minute unwatchable raw file and a 25-minute video people actually finish. On a construction site, roughly 30–50% of daytime footage is idle (lunch breaks, unmoving weather, off-camera work). Tweedia Studio detects that stillness automatically so you do not have to scrub through it by hand.',
  },
  {
    q: 'Can I change the video speed after I capture it?',
    a: 'Somewhat — you can re-render at a different FPS (48 FPS halves the length of a 24 FPS assemble), but you cannot recover detail that was not captured. Getting the interval right up front is the single biggest lever, which is what this calculator is for.',
  },
  {
    q: 'What interval should I use for a GoPro time lapse?',
    a: 'For a short outdoor scene, 2–5 seconds. For an all-day shoot, 10–30 seconds keeps the finished clip watchable. For anything running days or longer, treat the GoPro like a long-term camera — intervals in minutes, and constant power rather than a battery.',
    link: {
      id: 'gopro',
      to: '/guides/gopro-time-lapse-photos-to-video',
      label: 'Turning GoPro time lapse photos into a video →',
      navTarget: 'gopro_guide_from_calculator_faq',
    },
  },
];

// ---------- Component ----------
const TimelapseCalculatorPage = ({ user, signIn }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFromQuery = useMemo(
    () => stateFromQuery(searchParams, PUBLIC_DEFAULT_STATE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [state, setState] = useState(initialFromQuery);
  const [copiedLink, setCopiedLink] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const copyTimeoutRef = useRef(null);
  // Nothing is written to the URL until the visitor changes something. v1 wrote
  // the full default state into the query on mount, which made every landing
  // look like a shared link in analytics and left a 17-parameter URL in the
  // address bar of a page nobody had touched yet.
  const dirtyRef = useRef(false);
  // The driving inputs are seeded once per session, the first time someone
  // opens that scenario, so a later visit back to Driving keeps their numbers.
  const driveSeededRef = useRef(state.scenario === 'drive');

  // The flags doc requires auth; only subscribe when signed in, otherwise the
  // read is skipped and the hook returns defaultValue (on).
  const { enabled: saveEnabled } = useFeatureFlag('calcSaveToProjectV1', {
    defaultValue: true,
    subscribe: !!user,
  });

  // Analytics debounce for calculator_input_changed.
  const debounceRef = useRef({});
  const fireInputChanged = useCallback((control) => {
    if (debounceRef.current[control]) {
      clearTimeout(debounceRef.current[control]);
    }
    debounceRef.current[control] = setTimeout(() => {
      trackEvent('calculator_input_changed', { control });
      delete debounceRef.current[control];
    }, 500);
  }, []);
  useEffect(() => () => {
    Object.values(debounceRef.current).forEach(clearTimeout);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
  }, []);

  // Sync state -> URL, but only once the visitor has actually changed something.
  useEffect(() => {
    if (!dirtyRef.current) return;
    setSearchParams(stateToQuery(state), { replace: true });
  }, [state, setSearchParams]);

  // Resume the save flow after sign-in: the user hit "Create account & save",
  // authenticated in the app-level modal, and lands back here with a truthy
  // `user`. The full calculator state was preserved in the URL, so we only
  // need to re-open the dialog.
  useEffect(() => {
    if (user && saveEnabled && sessionStorage.getItem('tweedia:calcSavePending') === '1') {
      sessionStorage.removeItem('tweedia:calcSavePending');
      setSaveOpen(true);
    }
  }, [user, saveEnabled]);

  // JSON-LD schema graph (WebApplication + FAQPage). Memoized so
  // usePageMeta does not re-run its effect on every render.
  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Time Lapse Calculator',
        applicationCategory: 'MultimediaApplication',
        operatingSystem: 'Any',
        url: 'https://tweediastudio.com/tools/timelapse-calculator',
        description: 'Work out the final video length, the capture interval, or how long you need to record for a time lapse. Covers shoots from minutes to months and driving time lapses.',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        publisher: { '@type': 'Organization', name: 'Tweedia Technologies LLC' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  }), []);

  usePageMeta({
    title: 'Time Lapse Calculator: Video Length, Interval and Recording Time | Tweedia Studio',
    description: 'Free time lapse calculator. Get the video length, the interval, or how long to record, for shoots from seconds to months and for driving time lapses.',
    canonicalPath: '/tools/timelapse-calculator',
    jsonLd,
  });

  // ----- Update helpers (wired into the shared calculator core) -----
  const patch = useCallback((updates, control) => {
    dirtyRef.current = true;
    setState((s) => ({ ...s, ...updates }));
    if (control) fireInputChanged(control);
  }, [fireInputChanged]);

  const applyExample = useCallback((example) => {
    dirtyRef.current = true;
    setState((s) => ({ ...s, ...example.inputs }));
    trackEvent('calculator_preset_selected', { preset_id: example.id });
  }, []);

  const handleSolveForChange = useCallback((solveFor) => {
    trackEvent('calculator_mode_changed', { mode: solveFor });
  }, []);

  const handleScenarioChange = useCallback((scenario) => {
    trackEvent('calculator_scenario_changed', { scenario });
    if (scenario === 'drive' && !driveSeededRef.current) {
      driveSeededRef.current = true;
      setState((s) => {
        const untouched = s.distanceValue === PUBLIC_DEFAULT_STATE.distanceValue
          && s.speedValue === PUBLIC_DEFAULT_STATE.speedValue;
        return untouched ? { ...s, ...DRIVE_DEFAULTS } : s;
      });
    }
  }, []);

  // ----- CTAs -----
  const signedIn = !!user;
  const handleStudioCta = () => {
    trackEvent('cta_click', { cta_id: 'calc_try_studio', signed_in: signedIn });
    if (!signedIn) signIn();
  };
  const handleGuideClick = () => {
    trackEvent('nav_click', { target: 'guide_from_calculator' });
  };
  const handleNavClick = (target) => () => {
    trackEvent('nav_click', { target });
  };

  // ----- Copy link -----
  // Built from state rather than read off window.location, because the URL is
  // deliberately left bare until the first edit — a pristine page would
  // otherwise copy a link that carries none of what is on screen.
  const shareUrl = () => {
    const qs = new URLSearchParams(stateToQuery(state)).toString();
    if (typeof window === 'undefined') return `/tools/timelapse-calculator?${qs}`;
    return `${window.location.origin}${window.location.pathname}?${qs}`;
  };

  const handleCopyLink = async () => {
    trackEvent('cta_click', { cta_id: 'calc_copy_link', signed_in: signedIn });
    try {
      const url = shareUrl();
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      }
      setCopiedLink(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopiedLink(false), 1800);
    } catch (e) {
      // navigator.clipboard can throw in non-secure contexts; the button
      // still visually confirms so the user is not stuck.
      setCopiedLink(true);
    }
  };

  // ----- Save this plan (works signed-in OR out; dialog renders the state) -----
  const handleSavePlan = () => {
    trackEvent('cta_click', { cta_id: 'calc_save_plan', signed_in: signedIn });
    setSaveOpen(true);
  };

  const examples = EXAMPLES[state.scenario] || EXAMPLES.project;

  const presetRow = (
    <div className="calc-examples" data-testid="calc-examples">
      <span className="calc-examples-label">Try</span>
      <div className="calc-examples-row" role="group" aria-label="Example setups">
        {examples.map((ex) => (
          <button
            key={ex.id}
            type="button"
            className="calc-example"
            data-testid={`preset-${ex.id}`}
            onClick={() => applyExample(ex)}
          >
            <span className="calc-example-label">{ex.label}</span>
            <span className="calc-example-sub">{ex.sub}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const shareRow = (
    <div className="calc-share-row">
      {saveEnabled && (
        <button
          type="button"
          className="calc-cta-primary calc-save-btn"
          onClick={handleSavePlan}
          data-testid="calc-save-plan-btn"
        >
          Save this plan
        </button>
      )}
      <button
        type="button"
        className="calc-copy-btn"
        onClick={handleCopyLink}
        data-testid="copy-link-btn"
      >
        {copiedLink ? 'Copied' : 'Copy link'}
      </button>
    </div>
  );

  const ctaCard = (
    <div className="calc-cta-card" data-testid="calc-cta-card">
      <h3>Let the footage set the pace</h3>
      <p>
        Tweedia Studio finds the still stretches in your sequence for you. MotionTrim
        cuts them out, MotionPace speeds through them at up to {PACE_CEILING}× so the
        story stays intact, and you pick which one you want after you see both lengths.
      </p>
      {signedIn ? (
        <Link
          to="/studio"
          className="calc-cta-primary"
          onClick={handleStudioCta}
          data-testid="cta-studio"
        >
          Try Tweedia Studio free
        </Link>
      ) : (
        <button
          type="button"
          className="calc-cta-primary"
          onClick={handleStudioCta}
          data-testid="cta-studio"
        >
          Try Tweedia Studio free
        </button>
      )}
      {MOTIONPACE_MARKETING_ENABLED && (
        <Link
          to="/motionpace"
          className="calc-cta-secondary"
          onClick={handleNavClick('motionpace_from_calculator')}
          data-testid="cta-motionpace"
        >
          How MotionPace decides what to speed up →
        </Link>
      )}
      <Link
        to="/guides/long-term-timelapse"
        className="calc-cta-secondary"
        onClick={handleGuideClick}
        data-testid="cta-guide"
      >
        New to long-term timelapse? Read the full setup guide →
      </Link>
      <Link
        to="/guides/how-long-is-a-time-lapse"
        className="calc-cta-secondary"
        onClick={handleNavClick('howlong_from_calculator')}
        data-testid="cta-howlong"
      >
        How long will your time lapse be? See the conversion charts →
      </Link>
    </div>
  );

  return (
    <div className="calc-page">
      <Header user={user} signIn={signIn} />
      <main className="calc-main" data-testid="calc-main">
        {/* ---------- Hero ---------- */}
        <section className="calc-hero">
          <p className="calc-eyebrow">Free tool from Tweedia Studio</p>
          <h1 className="calc-hero-title">Time Lapse Calculator</h1>
          <p className="calc-hero-sub">
            Get the video length, the capture interval, or how long you need to record.
            It covers a two-hour sunset, a long-term construction build, and driving, and
            it shows what trimming or pacing the still footage does to the final length.
          </p>
        </section>

        {/* ---------- Shared calculator core ---------- */}
        <TimelapseCalculator
          value={state}
          onChange={patch}
          onSolveForChange={handleSolveForChange}
          onScenarioChange={handleScenarioChange}
          presetRow={presetRow}
          panelFooter={shareRow}
          asideFooter={ctaCard}
        />

        {saveOpen && (
          <SaveCalculationDialog
            state={state}
            user={user}
            onRequestSignIn={signIn}
            onClose={() => setSaveOpen(false)}
          />
        )}

        {/* ---------- Explainer ---------- */}
        <section className="calc-explainer" aria-labelledby="calc-explainer-heading">
          <h2 id="calc-explainer-heading">The math behind it</h2>
          <p>
            Everything here comes from three lines. Recording time is however long the
            camera runs: hours for a single shoot, days times the daily window for a
            long-term project, distance divided by average speed for a drive.
          </p>
          <ul className="calc-formula-list">
            <li>
              <strong>Video length</strong>
              <code>recording time ÷ interval ÷ fps</code>
            </li>
            <li>
              <strong>Interval</strong>
              <code>recording time ÷ (video length × fps)</code>
            </li>
            <li>
              <strong>Recording time</strong>
              <code>video length × fps × interval</code>
            </li>
          </ul>
          <p>
            When you solve for the interval we snap the answer to a stop you can actually
            dial in (0.5&nbsp;s, 1&nbsp;s, 2&nbsp;s, 5&nbsp;s and up to 2&nbsp;hours),
            picking the one that lands closest to your target length. Ties break toward
            the longer interval, which means fewer frames and less storage.
          </p>
          <p>
            Driving works in distance instead of time. Frames per mile ={' '}
            <code>3600 ÷ (speed × interval)</code>, so at 45&nbsp;mph with a
            1&nbsp;second interval you get 80 frames a mile, one every 20&nbsp;metres.
            The bigger that gap, the more the road strobes.
          </p>
          <p>
            The still-footage share is the part of the recording where nothing visible
            changes. Cutting it is what MotionTrim does, so that finish is the raw length
            times what is left. MotionPace keeps it but plays it fast, up to {PACE_CEILING}×
            by default, so its length sits between the two. Storage uses your MB per frame.
          </p>
        </section>

        {/* ---------- FAQ ---------- */}
        <section className="calc-faq" aria-labelledby="calc-faq-heading" data-testid="calc-faq">
          <h2 id="calc-faq-heading">Frequently asked questions</h2>
          <div className="calc-faq-list">
            {FAQ.map((item) => (
              <details key={item.q} className="calc-faq-item">
                <summary>{item.q}</summary>
                <p>
                  {item.a}
                  {item.link && (
                    <>
                      {' '}
                      <Link
                        to={item.link.to}
                        onClick={handleNavClick(item.link.navTarget)}
                        data-testid={`faq-link-${item.link.id}`}
                      >
                        {item.link.label}
                      </Link>
                    </>
                  )}
                </p>
              </details>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TimelapseCalculatorPage;
